import React, { useCallback } from "react";
import { Button, Card, Flex, Label } from "@jsq/ds/core";
import { Form, FormTextInput, FormDatePicker, SubmitButton } from "@jsq/ds/forms";
import { Column } from "@jsq/ds/layouts";
import { useDistributionWorkflowContext } from "../../Context/context";
import { DistributionWorkflowStepProps } from "../../types";
import { MakerNoteTextarea } from "./MakerNoteTextarea";
import {
  InitialValues,
  PaymentDetailsInputObject,
  PaymentDetailsStepInputFields,
} from "./schema";
import { datePickerContainer, label, paymentDescriptionContainer } from "./styles_vanilla.css";
import { filterAvailableDate, getNextSwiftProcessingDateInLocal } from "./utils";

export const PaymentDetailsStep: React.FC<DistributionWorkflowStepProps> = ({
  onBack,
  onContinue,
}: DistributionWorkflowStepProps) => {
  const {
    setPaymentDescription,
    setScheduledPaymentProcessDate,
    setMakerNote,
    paymentDescription,
    scheduledPaymentProcessDate,
    makerNote,
    selectedPositions,
    numberOfAvailablePositions,
  } = useDistributionWorkflowContext();

  const positionsExcluded = numberOfAvailablePositions > selectedPositions.length;

  const nextAvailableProcessingDate = getNextSwiftProcessingDateInLocal();
  const onSubmit = useCallback(
    (values: InitialValues) => {
      // Note: We pass through the Date as is in this component. This both to abstract out
      // the business logic of processing the payment date at PaymentBatch creation time as
      // well as preventing the need to re-convert back to the user's selected date during the
      // review step.
      if (values.scheduledPaymentProcessDate) {
        setScheduledPaymentProcessDate(values.scheduledPaymentProcessDate);
      }
      setPaymentDescription(values.paymentDescription);
      setMakerNote(values.makerNote?.trim());
      onContinue();
    },
    [onContinue, setPaymentDescription, setScheduledPaymentProcessDate, setMakerNote],
  );

  const paymentDetailsInitialValues: InitialValues = {
    paymentDescription: paymentDescription ?? "",
    scheduledPaymentProcessDate: scheduledPaymentProcessDate,
    makerNote: makerNote ?? "",
  };

  return (
    <Column size={"md"}>
      <Card title="Payment details">
        <Form
          onSubmit={onSubmit}
          submitOnlyChangedValues={false}
          initialValues={paymentDetailsInitialValues}
          validationSchema={PaymentDetailsInputObject(positionsExcluded)}
        >
          <Flex flexDirection="column">
            <div className={datePickerContainer}>
              <Label className={label}>Payment date</Label>
              <FormDatePicker
                name={PaymentDetailsStepInputFields.ScheduledPaymentProcessDate}
                hasIcon
                isFull={false}
                dateFormat="MM/dd/yyyy"
                placeholderText="Select date"
                filterDate={(date: Date) =>
                  filterAvailableDate(date, nextAvailableProcessingDate)
                }
                openToDate={nextAvailableProcessingDate.toJSDate()}
              />
            </div>
            <FormTextInput
              className={paymentDescriptionContainer}
              label="Payment description for recipients"
              name={PaymentDetailsStepInputFields.PaymentDescription}
              helpText={"35 characters max"}
            />
            <MakerNoteTextarea />
            <Flex flexDirection="row-reverse" gap={10}>
              <SubmitButton data-testid="continue-button">Continue</SubmitButton>
              <Button onClick={onBack} variant={"neutral"} data-testid="back-button">
                Back
              </Button>
            </Flex>
          </Flex>
        </Form>
      </Card>
    </Column>
  );
};
